import { t, useLocale, getLocale, plural } from "@/i18n/locale";
import { Message } from "@/i18n/Message";
import { Heart, Play, MessageCircle } from "lucide-react";
import {
  formatRelativeTime,
  parseApiTimestamp,
} from "@/components/runs/feedItem";
import { formatDiscoveryCount } from "./discoveryCatalogue";
import type { DiscoveryCardStats } from "./types";

/** Card metadata never turns an unavailable metric into a measured zero. */
export default function DiscoverCardMetrics({
  title,
  stats,
  publishedAt,
  compact = false,
}: {
  title: string;
  stats: DiscoveryCardStats;
  publishedAt: string | null;
  compact?: boolean;
}) {
  useLocale();
  const { discoverRunCount, likeCount, commentCount } = stats;
  const parsed = publishedAt ? parseApiTimestamp(publishedAt) : null;
  const date = parsed && Number.isFinite(parsed.getTime()) ? parsed : null;
  const timestamp = date
    ? t("Published {{value0}}", { value0: date.toLocaleString(getLocale()) })
    : t("Publication time unavailable");
  return (
    <div className="discovery-card-metrics">
      <div
        className="discovery-card-counts"
        role="group"
        aria-label={t("Statistics for {{value0}}", { value0: title })}
      >
        <span
          aria-label={
            likeCount == null ? t("Likes unavailable") : t("{{value0}} likes", { value0: likeCount })
          }
          title={t("Likes")}
        >
          <Heart size={12} aria-hidden="true" />
          {formatDiscoveryCount(likeCount)}
        </span>
        <span
          aria-label={
            discoverRunCount == null
              ? t("Runs unavailable")
              : plural(discoverRunCount, "{{value0}} run", "{{value0}} runs", { value0: discoverRunCount })
          }
          title={t("Discover sessions that became usable")}
        >
          <Play size={12} aria-hidden="true" />
          {discoverRunCount == null
            ? compact ? "—" : t("Runs unavailable")
            : compact ? formatDiscoveryCount(discoverRunCount) : plural(discoverRunCount, "{{value0}} run", "{{value0}} runs", { value0: formatDiscoveryCount(discoverRunCount) })}
        </span>
        {!compact && <span
          aria-label={
            commentCount == null
              ? t("Comments unavailable")
              : t("{{value0}} comments", { value0: commentCount })
          }
          title={t("Comments")}
        >
          <MessageCircle size={12} aria-hidden="true" />
          {formatDiscoveryCount(commentCount)}
        </span>}
      </div>
      {!compact && (date ? (
        <time
          dateTime={date.toISOString()}
          title={timestamp}
          aria-label={timestamp}
        >
          {formatRelativeTime(publishedAt!)}
        </time>
      ) : (
        <span className="discovery-card-time-unavailable" title={timestamp}><Message message={"Time unavailable"} /></span>
      ))}
    </div>
  );
}
